import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaPlus, FaTrash, FaArrowLeft, FaEdit, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';
import { useToast } from '../components/ui/ToastProvider';

const AddProjects = () => {
  const [projects, setProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [file, setFile] = useState(null);
  const [formData, setFormData] = useState({
    projectname: '',
    projectlink: '',
    projectdescription: '',
    projectimg: '',
  });
  const navigate = useNavigate();
  const { push } = useToast();

  // Live character count for description (max 200 chars)
  const desc = formData.projectdescription || '';
  const charCount = desc.length;
  const MAX_DESC_CHARS = 200;
  const descTooLong = charCount > MAX_DESC_CHARS;

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get('/getprojects');
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Hard character limit guard
      if (descTooLong) {
        alert(`Description must be ${MAX_DESC_CHARS} characters or fewer.`);
        return;
      }

      const fd = new FormData();
      fd.append('projectname', formData.projectname);
      fd.append('projectlink', formData.projectlink || '');
      fd.append('projectdescription', formData.projectdescription || '');
      if (file) {
        fd.append('file', file);
      }
      if (editingProject) {
        fd.append('projectid', editingProject.projectid);
        // Keep existing image on server if no new file provided
        if (!file && typeof formData.projectimg !== 'undefined') {
          fd.append('existingimage', formData.projectimg || '');
        }
        await axios.post('/editproject', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await axios.post('/addproject', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setShowForm(false);
      setEditingProject(null);
      setFile(null);
      setFormData({ projectname: '', projectlink: '', projectdescription: '', projectimg: '' });
      fetchProjects();
      push(editingProject ? 'Project updated successfully.' : 'Project added successfully.', { type: 'success' });
    } catch (error) {
      const msg = error?.response?.data?.error || error?.response?.data?.details || error?.message || 'Unknown error';
      console.error('Error saving project:', error?.response?.data || error);
      push(`Failed to save project: ${msg}`, { type: 'error', duration: 5000 });
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      projectname: project.projectname,
      projectlink: project.projectlink || '',
      projectdescription: project.projectdescription,
      projectimg: project.projectimg || '',
    });
    setFile(null);
    setShowForm(true);
  };

  const handleDelete = async (projectid) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await axios.get(`/delete_project?projectid=${projectid}`);
        fetchProjects();
        push('Project deleted.', { type: 'success' });
      } catch (error) {
        console.error('Error deleting project:', error);
        push('Failed to delete project.', { type: 'error' });
      }
    }
  };

  return (
    <div className="min-h-screen p-8">
      <SEO title="Manage Projects – SDC" description="Add, edit, and organize student software projects for the SDC showcase." canonical="https://sdc.mitsgwalior.in/showprojects" />
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <motion.button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 glass rounded-lg flex items-center space-x-2 hover:bg-white/10"
            whileHover={{ scale: 1.05 }}
          >
            <FaArrowLeft />
            <span>Back</span>
          </motion.button>

          <h1 className="text-3xl font-bold gradient-text">Manage Projects</h1>

          <motion.button
            onClick={() => {
              setShowForm(!showForm);
              setEditingProject(null);
              setFormData({ projectname: '', projectlink: '', projectdescription: '', projectimg: '' });
            }}
            className="px-6 py-2 bg-gradient-to-r from-primary to-secondary rounded-lg flex items-center space-x-2"
            whileHover={{ scale: 1.05 }}
          >
            <FaPlus />
            <span>Add Project</span>
          </motion.button>
        </div>

        {showForm && (
          <motion.form
            onSubmit={handleSubmit}
            className="glass rounded-xl p-6 mb-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{editingProject ? 'Edit Project' : 'Add New Project'}</h2>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingProject(null);
                  setFile(null);
                  setFormData({ projectname: '', projectlink: '', projectdescription: '', projectimg: '' });
                }}
                className="text-gray-400 hover:text-white"
              >
                <FaTimes size={20} />
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <input
                type="text"
                placeholder="Project Name"
                value={formData.projectname}
                onChange={(e) => setFormData({ ...formData, projectname: e.target.value })}
                className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-primary"
                required
              />
              <input
                type="url"
                placeholder="Project Link"
                value={formData.projectlink}
                onChange={(e) => setFormData({ ...formData, projectlink: e.target.value })}
                className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-primary"
              />
            </div>
            <div className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg mb-4">
              <label className="block text-sm text-gray-300 mb-2">Project Image {editingProject ? '(leave empty to keep existing)' : ''}</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                  setFile(f);
                }}
                className="block w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary/30 file:text-white hover:file:bg-primary/40"
              />
            </div>
            <textarea
              placeholder={`Project Description (max ${MAX_DESC_CHARS} characters)`}
              value={formData.projectdescription}
              onChange={(e) => setFormData({ ...formData, projectdescription: e.target.value.slice(0, MAX_DESC_CHARS) })}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-primary mb-2"
              rows="3"
              maxLength={MAX_DESC_CHARS}
            />
            <div className="mb-4 text-xs text-gray-400">
              <span className={descTooLong ? 'text-red-400' : ''}>Characters: {charCount}/{MAX_DESC_CHARS}</span>
            </div>
            {(file || formData.projectimg) && (
              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-2">Image Preview</label>
                <img
                  src={
                    file
                      ? URL.createObjectURL(file)
                      : (formData.projectimg && (formData.projectimg.startsWith('http') ? formData.projectimg : `/files/${formData.projectimg}`))
                  }
                  alt="Preview"
                  className="w-full h-48 object-cover rounded-lg border border-white/10"
                  onError={(e) => {
                    e.target.src = '';
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
            <button
              type="submit"
              disabled={descTooLong}
              title={descTooLong ? `Max ${MAX_DESC_CHARS} characters allowed` : ''}
              className={`px-6 py-2 rounded-lg ${descTooLong ? 'bg-gray-600 cursor-not-allowed' : 'bg-gradient-to-r from-primary to-secondary'}`}
            >
              {editingProject ? 'Update Project' : 'Add Project'}
            </button>
          </motion.form>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <motion.div
              key={project.projectid}
              className="glass rounded-xl p-6"
              whileHover={{ y: -5 }}
            >
              {project.projectimg && (
                <img
                  src={project.projectimg && (project.projectimg.startsWith('http') ? project.projectimg : `/files/${project.projectimg}`)}
                  alt={project.projectname}
                  className="w-full h-40 object-cover rounded-lg mb-4"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <h3 className="text-xl font-bold mb-2">{project.projectname}</h3>
              <p className="text-gray-400 text-sm mb-4 line-clamp-3">{project.projectdescription}</p>
              {project.projectlink && (
                <a
                  href={project.projectlink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary text-sm hover:underline block mb-4"
                >
                  View Project →
                </a>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(project)}
                  className="flex-1 px-4 py-2 bg-primary/20 hover:bg-primary/30 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                >
                  <FaEdit />
                  <span>Edit</span>
                </button>
                <button
                  onClick={() => handleDelete(project.projectid)}
                  className="flex-1 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                >
                  <FaTrash />
                  <span>Delete</span>
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AddProjects;
